import { useQueryClient } from '@tanstack/react-query';
import MessageResponse from 'model/MessageTypes/Responses';
import Pusher from 'pusher-js';
import React, { useCallback, useEffect, useRef } from 'react';
import initializePusher from 'services/Pusher';
import {
  HostScore,
  ScoringEvent,
  getHostMetricsLevelLabelAndIcon,
  getHostScoringTypeLabel,
} from 'utils/HostMetrics';
import Logger from 'utils/Logger';
import { useAccount } from '~/authentication/store';
import toast from '~/reusable-components/notifications/toast';

import { CombinedActionTypes, CombinedActions } from './actionsFactory';
import { State } from './combinedReducer';

interface PusherHandlerProps {
  dispatch: (options: CombinedActionTypes) => void;
  actions: CombinedActions;
  state: State;
}

const getPresenceChannelName = (
  accountId: string,
  otherAccountId: string,
): string => {
  const parts = [accountId, otherAccountId].sort();
  return `presence-${parts[0]}.${parts[1]}`;
};

export const usePusher = () => {
  const socketRef = useRef<Pusher>(initializePusher());

  const subscribePresenceChannel = useCallback(
    (accountId: string, otherAccountId: string) => {
      if (!accountId || !otherAccountId) {
        console.error(
          '[Pusher.subscribePresenceChannel] Missing accountId or otherAccountId',
        );
        return;
      }
      const channelName = getPresenceChannelName(accountId, otherAccountId);
      socketRef.current.subscribe(channelName);
    },
    [],
  );
  const unsubscribePresenceChannel = useCallback(
    (accountId: string, otherAccountId: string) => {
      if (!accountId || !otherAccountId) {
        console.error(
          '[Pusher.unsubscribePresenceChannel] Missing accountId or otherAccountId',
        );
        return;
      }
      const channelName = getPresenceChannelName(accountId, otherAccountId);
      socketRef.current.unsubscribe(channelName);
      Logger.log(`Unsubscribed from ${channelName}`);
    },
    [],
  );

  return {
    pusher: socketRef.current,
    subscribePresenceChannel,
    unsubscribePresenceChannel,
  };
};

const PusherHandler = ({
  accountId,
  state,
  actions,
  dispatch,
}: PusherHandlerProps & { accountId: string }) => {
  const { pusher } = usePusher();
  const queryClient = useQueryClient();

  const updateReducers = useCallback(
    (message: MessageResponse) => {
      if (message.from === accountId && message.status === 'READ') {
        actions.messages.markOwnConversationAsRead({
          accountId: accountId,
          contactId: message.to,
        });
      } else if (!state.contacts[message.from]) {
        actions.chatContacts.fetchContacts({ page: 1, pageSize: 10 });
        actions.actionsRequired.fetchActionsRequired({
          page: 1,
          pageSize: 10,
        });
      } else {
        dispatch({ type: 'MESSAGES:NEW_MESSAGE', payload: message });
      }

      const messageCopy: any = message;
      if (messageCopy.trip_id && state.bookings[messageCopy.trip_id]) {
        void queryClient.prefetchQuery({
          queryKey: ['booking', messageCopy.trip_id],
        });
      }

      // Sometimes a message is pushed with only and id property, this can happen when a booking comes in.
      if (messageCopy.trip_id || Object.keys(messageCopy).length === 1) {
        actions.upcomingBookings.fetchUpcomingBookings({
          page: 1,
          pageSize: 10,
        });
      }
    },
    [
      accountId,
      actions.actionsRequired,
      actions.chatContacts,
      actions.messages,
      actions.upcomingBookings,
      dispatch,
      queryClient,
      state.bookings,
      state.contacts,
    ],
  );

  const showNewScoringEventNotification = ({ type, points }: ScoringEvent) => {
    toast({
      text: `You have ${
        points > 0 ? 'earned' : 'lost'
      } ${points} points in your ${getHostScoringTypeLabel(type)} score!`,
      icon: points > 0 ? '✅' : '😮‍💨',
    });
  };

  const showHostScoreLevelUpdatedNotification = ({
    level,
    type,
  }: HostScore) => {
    const { label, icon } = getHostMetricsLevelLabelAndIcon(level);
    toast({
      text: `You have reached ${label} level for ${getHostScoringTypeLabel(
        type,
      )}!`,
      icon,
    });
  };

  useEffect(() => {
    const socket = pusher;

    const channel = socket.subscribe(`private-${accountId}`);
    channel.bind('NewMessage', (message: MessageResponse) =>
      updateReducers(message),
    );
    channel.bind('UpdatedMessage', (message: MessageResponse) =>
      updateReducers(message),
    );
    channel.bind('TripUpdate', (message: MessageResponse) =>
      updateReducers(message),
    );
    channel.bind('NewScoringEvent', showNewScoringEventNotification);
    channel.bind(
      'HostScoreLevelUpdated',
      showHostScoreLevelUpdatedNotification,
    );

    return () => {
      socket.unsubscribe(`private-${accountId}`);
    };
  }, [pusher, accountId, updateReducers]);

  return null;
};

function PusherHandlerRenderer({
  actions,
  dispatch,
  state,
}: PusherHandlerProps) {
  const { account } = useAccount();

  if (account?.id) {
    return (
      <PusherHandler
        dispatch={dispatch}
        actions={actions}
        state={state}
        accountId={account.id}
      />
    );
  }
  return null;
}

export default PusherHandlerRenderer;
